<template>
	<Default :loading="loading">
		<div class="w-full h-full relative md:flex">
			<div class="grow flex flex-col md:translate-x-0 transform transition-transform duration-300 ease-in-out translate-x-0 h-full">
				<!-- Container -->
				<div class="grow p-3 bg-gray-100">
					<!-- Page header -->
					<div class="md:flex md:items-center">
						<!-- Left: Title -->
						<h1 class="text-2xl md:text-3xl text-gray-800 font-bold mb-2 md:mb-0 flex-1">Reporte</h1>
					</div>

					<div class="flex flex-col md:flex-row md:items-end justify-end mt-4 gap-2">
						<div>
							<input type="text" class="form-input" v-model="options.q" placeholder="Buscar por nombre, email..." @input="resetSkip" />
						</div>
						<div>
							<small class="w-full p-1">Desde</small>
							<input class="w-full form-input" type="datetime-local" name="date>" v-model="options['date>']" />
						</div>
						<div>
							<small class="w-full p-1">Hasta</small>
							<input class="w-full form-input" type="datetime-local" name="date<" v-model="options['date<']" />
						</div>
					</div>

					<!-- Table -->
					<div class="bg-white rounded-sm border border-gray-200 relative mt-3">
						<div class="md:flex md:items-center py-2 px-4">
							<header class="mb-2 md:mb-0 flex-1 font-semibold text-slate-800">
								Resultados:
								<span class="text-slate-400">{{ total }}</span>
							</header>
							<div class="gap-2 flex items-center">
								<div>
									Mostrar:
									<select v-model="options.limit" class="ml-2 form-select flex-1 md:flex-auto" @change="resetSkip">
										<option :value="5">5</option>
										<option :value="10">10</option>
										<option :value="20">20</option>
										<option :value="50">50</option>
										<option :value="100">100</option>
										<option :value="150">150</option>
									</select>
								</div>
							</div>
						</div>

						<!-- Table -->
						<div class="overflow-x-auto" v-if="!loading && items?.length">
							<table class="table-auto w-full">
								<!-- Table header -->
								<thead class="text-xs font-semibold uppercase text-gray-500 bg-gray-50 border-b border-t border-gray-200">
									<tr>
										<th class="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
											<div class="font-semibold text-left">
												<button class="flex items-center" @click="handleSort('name')">
													<p class="font-semibold text-left uppercase mr-1">Usuario</p>
													<icon scale="0.8" name="ri-arrow-up-down-line" v-if="sortBy.field != 'name'" />
													<icon scale="0.8" name="ri-sort-asc" v-else-if="sortBy.type === ''" />
													<icon scale="0.8" name="ri-sort-desc" v-else-if="sortBy.type === '-'" />
												</button>
											</div>
										</th>
										<th class="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
											<div class="font-semibold text-left">
												<button class="flex items-center" @click="handleSort('active')">
													<p class="font-semibold text-left uppercase mr-1">Estado</p>
													<icon scale="0.8" name="ri-arrow-up-down-line" v-if="sortBy.field != 'active'" />
													<icon scale="0.8" name="ri-sort-asc" v-else-if="sortBy.type === ''" />
													<icon scale="0.8" name="ri-sort-desc" v-else-if="sortBy.type === '-'" />
												</button>
											</div>
										</th>
										<th class="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
											<div class="font-semibold text-left">
												<button class="flex items-center" @click="handleSort('email')">
													<p class="font-semibold text-left uppercase mr-1">Email</p>
													<icon scale="0.8" name="ri-arrow-up-down-line" v-if="sortBy.field != 'email'" />
													<icon scale="0.8" name="ri-sort-asc" v-else-if="sortBy.type === ''" />
													<icon scale="0.8" name="ri-sort-desc" v-else-if="sortBy.type === '-'" />
												</button>
											</div>
										</th>
										<th class="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
											<div class="font-semibold text-left">
												<p class="font-semibold text-left uppercase mr-1">Acciones</p>
											</div>
										</th>
									</tr>
								</thead>
								<!-- Table body -->
								<tbody class="text-sm divide-y divide-gray-200">
									<tr class="even:bg-gray-100 hover:bg-gray-200" v-for="item in items" :key="item._id">
										<td class="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">{{ item.name }} {{ item.surname }}</td>
										<td class="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
											<div class="inline-flex font-medium rounded-full text-center px-2.5 py-0.5" :class="item.active ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-500'">
												<span v-if="item.active">Activo</span>
												<span v-else>Inactivo</span>
											</div>
										</td>
										<td class="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
											{{ item.email }}
										</td>
										<td class="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
											<Button :disabled="!user.permissions.includes('user.report.download')" :loading="downloading.state && downloading.id === item._id" @click="download(item)" loading-label="">
												<OhVueIcon name="ri-file-download-line"></OhVueIcon>
											</Button>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>

					<!-- Pagination -->
					<div class="mt-8 mb-24" v-if="!loading && items?.length">
						<Pagination v-show="total > options.limit" :results="items.length" :skip="options.skip" :limit="options.limit" :total="total" @page="updatePage" />
					</div>

					<!-- No results -->
					<div class="bg-white p-4 rounded-sm border border-gray-200 mt-3" v-if="(!loading && !items?.length) || !!error.message">
						<Banner type="error" :open="!!error?.message" fixed>
							<p>{{ error.message }}</p>
							<ul v-if="error?.errors">
								<li v-for="(err, i) in error?.errors" :key="i">
									<p class="p-3">- {{ err.message }}</p>
								</li>
							</ul>
						</Banner>
						<div class="w-full flex justify-center p-4" v-if="!loading && !items?.length">
							<div class="text-center py-6">
								<div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-t from-red-300 to-red-100 mb-4">
									<icon name="ri-information-line" scale="2" />
								</div>
								<h2 class="text-2xl text-gray-800 font-bold mb-2">No se han encontrado usuarios.</h2>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</Default>
</template>

<script setup lang="ts">
import { ValidationError } from '@/errors/ValidationError'
import { dayjs } from '@/services/dayjs'
import { user } from '@/user'
import { Ref, computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Banner from '../../components/Banner.vue'
import Pagination from '../../components/Pagination.vue'
import { Project } from '../../interfaces/Project'
import { get } from '../../services/api'
import { queryRegExpToStr } from '../../utils/queryRegExpToStr'
import { strToRegExp } from '../../utils/strToRegExp'
import Default from '../Default.vue'
import Button from '../../components/Button.vue'
import { OhVueIcon } from 'oh-vue-icons'
import { User } from '../../interfaces/User'

const route = useRoute()
const options: Record<string, any> = reactive({
	skip: route.query.skip ? parseInt(<string>route.query.skip) : 0,
	sort: route.query.sort || 'name',
	limit: route.query.limit ? parseInt(<string>route.query.limit) : 20,
	q: route.query.q ? queryRegExpToStr(route.query.q.toString()) : undefined,
	'date<': route.query['date<'] ? dayjs(route.query['date<'].toString()).format('YYYY-MM-DDTHH:mm') : undefined,
	'date>': route.query['date>'] ? dayjs(route.query['date>'].toString()).format('YYYY-MM-DDTHH:mm') : undefined
})

const downloading = ref({
	state: false,
	id: null
})
const loading = ref(false)
const items = ref([])
const router = useRouter()
const total = ref(0)
const error = ref<ValidationError>(<any>{})
const sortBy = reactive({ field: (<string>options.sort || '')?.split('-')[0], type: options.sort?.includes('-') ? '-' : '' })

const query = computed(() => {
	return {
		...route.query,
		...options,
		q: options.q ? strToRegExp(options.q) : undefined,
		'date<': options['date<'] ? dayjs(options['date<']).toISOString() : undefined,
		'date>': options['date>'] ? dayjs(options['date>']).toISOString() : undefined
	}
})

const search = async () => {
	loading.value = true
	error.value = <any>{}
	try {
		await router.replace({
			query: query.value
		})

		const { 'date<': dateFrom, 'date>': dateTo, ...sendQuery } = query.value

		items.value = []
		const { data, pagination } = await get({ name: 'user', query: sendQuery })
		total.value = pagination.total
		items.value = data
	} catch (err) {
		error.value = err
	}
	loading.value = false
}

const handleSort = (column: string) => {
	if (column != sortBy.field) {
		sortBy.field = column
		sortBy.type = ''
	} else {
		sortBy.type = sortBy.type === '-' ? '' : '-'
	}
	options.sort = `${sortBy.type}${sortBy.field}`
}

const download = async (item: User) => {
	downloading.value.state = true
	downloading.value.id = item._id
	try {
		const sendQuery = {}
		if (query.value['date<']) {
			sendQuery['date<'] = query.value['date<']
		}
		if (query.value['date>']) {
			sendQuery['date>'] = query.value['date>']
		}
		const { data } = await get({ name: 'user.report.download', params: { _id: item._id }, query: sendQuery })

		const base64Uri = `data:application/pdf;base64,${data}`
		const pdfBlob = await fetch(base64Uri).then(res => res.blob())

		const link = document.createElement('a')
		link.href = window.URL.createObjectURL(pdfBlob)
		link.download = `INFORME_GENERICO_${item.name}_${item.surname}_${dayjs().format('DD-MM-YYYY')}.pdf`
		link.click()
		link.remove()
	} catch (err) {
		error.value = err
	}

	downloading.value.state = false
	downloading.value.id = null
}

const resetSkip = () => {
	options.skip = 0
}

const updatePage = (page: Ref) => {
	options.skip = page.value == -1 ? 0 : (page.value - 1) * options.limit
}

watch([() => options.q, () => options.sort, () => options.limit, () => options.skip], () => {
	search()
})

onMounted(() => {
	search()
})
</script>
